/**
 * Every script, suggestion, and version document crosses the wire as an
 * opaque JSON string and gets parsed inside a render body. An unguarded
 * `JSON.parse` of a corrupt, truncated, or schema-incompatible blob throws
 * during render, which unmounts the whole tree — for a client with no console
 * that's a permanent blank screen with nothing to act on.
 *
 * All document parses go through here instead. Callers treat `null` as "this
 * document can't be shown" and render a recoverable state; ErrorBoundary is
 * the last resort behind this, not the first line of defence.
 */

/** The shape every stored document has: a ProseMirror `doc` node. */
export interface DocJson {
  type: 'doc';
  content?: unknown[];
}

export function parseDocJson(doc_json: string | null | undefined): DocJson | null {
  if (!doc_json) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(doc_json);
  } catch {
    return null;
  }

  // JSON.parse happily returns strings, numbers, and arrays; those reach
  // `nodeFromJSON` and throw there instead, so reject anything that isn't a
  // plausible top-level document node here.
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const doc = parsed as { type?: unknown; content?: unknown };
  if (doc.type !== 'doc') return null;
  if (doc.content !== undefined && !Array.isArray(doc.content)) return null;

  return doc as DocJson;
}
